import { useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { UploadCloud, CheckCircle2, CircleAlert } from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../services/api';
import LoadingSpinner from '../components/Shared/LoadingSpinner';
import PageHeader from '../components/Shared/PageHeader';
import { fmtNumber, fmtDate } from '../components/Sales/format';
import { c, btn, card } from '../theme';

// The three mapping files behind the Sales Report's Cost Center resolution.
// Each upload becomes the current version for the NEXT sales export; periods
// already committed keep the versions they were resolved with, so replacing a
// file here never rewrites a published week.
export default function SalesMappings() {
  const { data = [], isLoading, error } = useQuery({
    queryKey: ['sales-references'],
    queryFn:  () => api.get('/sales-report/references'),
  });

  return (
    <div style={s.page}>
      <PageHeader
        eyebrow="Mappings"
        title="Sales Mappings"
        subtitle="The files that place each sales line in a cost center: the Workday Extract File first, then the Routes with X-Over export, then City Mapping as the last resort. Replace one whenever its source system changes. A new file applies to the next sales upload; periods already committed are not restated."
      />
      {isLoading ? <LoadingSpinner /> : error ? <div style={s.error}>{error.message}</div> : (
        <div style={s.grid}>
          {data.map((ref) => <ReferenceCard key={ref.kind} reference={ref} />)}
        </div>
      )}
    </div>
  );
}

function ReferenceCard({ reference }) {
  const qc = useQueryClient();
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const current = reference.current;

  const upload = useMutation({
    mutationFn: () => {
      const fd = new FormData();
      fd.append('file', file);
      return api.upload(`/sales-report/references/${reference.kind}`, fd);
    },
    onSuccess: ({ version, previous }) => {
      // The row count against the previous version is the cheap sanity check:
      // a Cross Over export that arrives with half its routes is otherwise only
      // noticed as a pile of Unresolved lines on the next upload.
      toast.success(
        `${reference.label} replaced: ${fmtNumber(version.row_count)} rows`
        + (previous ? ` (was ${fmtNumber(previous.row_count)}).` : '.')
      );
      setFile(null);
      if (inputRef.current) inputRef.current.value = '';
      qc.invalidateQueries({ queryKey: ['sales-references'] });
    },
    onError: (err) => toast.error(err.message || 'Upload failed.'),
  });

  return (
    <section style={s.card}>
      <div style={s.head}>
        {current ? <CheckCircle2 size={16} color={c.success} /> : <CircleAlert size={16} color={c.warning} />}
        <div style={s.title}>{reference.label}</div>
      </div>
      <div style={s.source}>{reference.source}</div>
      <div style={s.columns}>
        Needs columns: {reference.columns.map((col) => <code key={col} style={s.code}>{col}</code>)}
      </div>

      <div style={s.current}>
        {current ? (
          <>
            <div style={s.currentName} title={current.file_name}>{current.file_name}</div>
            <div style={s.currentMeta}>
              {fmtNumber(current.row_count)} rows · {fmtDate(current.created_at)}
              {current.uploaded_by_name ? ` · ${current.uploaded_by_name}` : ''}
            </div>
          </>
        ) : (
          <div style={s.missing}>Not uploaded — sales exports cannot be loaded until it is.</div>
        )}
      </div>

      <input
        ref={inputRef} type="file" accept=".csv,.xlsx,.xls" style={{ display: 'none' }}
        onChange={(e) => setFile(e.target.files[0] || null)}
      />
      <div style={s.actions}>
        <button type="button" style={s.secondaryBtn} onClick={() => inputRef.current?.click()} disabled={upload.isPending}>
          <UploadCloud size={14} /> {file ? file.name : 'Choose file'}
        </button>
        <button
          type="button"
          style={{ ...s.primaryBtn, opacity: file && !upload.isPending ? 1 : 0.5 }}
          disabled={!file || upload.isPending}
          onClick={() => upload.mutate()}
        >
          {upload.isPending ? 'Uploading…' : current ? 'Replace' : 'Upload'}
        </button>
      </div>
    </section>
  );
}

const s = {
  page:  { maxWidth: '1100px', margin: '0 auto' },
  grid:  { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '14px' },
  card:  { ...card.sm, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '7px' },
  head:  { display: 'flex', alignItems: 'center', gap: '7px' },
  title: { fontSize: '13px', fontWeight: 600, color: c.text },
  source:{ fontSize: '11.5px', color: c.textMuted },
  columns: { fontSize: '10.5px', color: c.textFaint, display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center' },
  code:  { fontFamily: "ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace", fontSize: '10.5px', background: c.canvas, borderRadius: '4px', padding: '1px 5px', color: c.textStrong },
  current: { ...card.sunk, padding: '11px 12px', marginTop: '4px' },
  currentName: { fontSize: '12px', fontWeight: 600, color: c.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  currentMeta: { fontSize: '10.5px', color: c.textMuted, marginTop: '2px' },
  missing: { fontSize: '11.5px', color: c.warningText },
  actions: { display: 'flex', gap: '8px', marginTop: '4px', flexWrap: 'wrap' },
  primaryBtn:   { ...btn.primarySm },
  secondaryBtn: { ...btn.secondarySm, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' },
  error: { color: c.dangerText, fontSize: '12.5px' },
};
